import { inject, Injectable } from '@angular/core';
import { CommonIP } from '@model/ftf-api.model';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AccountService } from '@service/account.service';
import { SettingsService } from '@service/settings.service';
import { of, switchMap } from 'rxjs';

import * as HistoryActions from '../actions/history.actions';

@Injectable()
export class HistoryEffects {
  private actions$ = inject(Actions);
  private settingsService = inject(SettingsService);
  private accountService = inject(AccountService);

  loadHistory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HistoryActions.loadHistory),
      switchMap(() => {
        try {
          const userName = this.accountService.userName || 'default';
          const clientName = this.accountService.clientName || 'default';
          const key = this.settingsService.generateHexKey(userName, clientName, 'history');
          const historyJson = localStorage.getItem(key);
          const items: CommonIP[] = historyJson ? JSON.parse(historyJson) : [];
          return of(HistoryActions.loadHistorySuccess({ items }));
        } catch (error) {
          return of(HistoryActions.loadHistoryFailure({ error }));
        }
      })
    )
  );

  addHistoryItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HistoryActions.addHistoryItem),
      switchMap(({ item }) => {
        try {
          const userName = this.accountService.userName || 'default';
          const clientName = this.accountService.clientName || 'default';
          const key = this.settingsService.generateHexKey(userName, clientName, 'history');

          const historyJson = localStorage.getItem(key);
          const history: CommonIP[] = historyJson ? JSON.parse(historyJson) : [];

          const isDuplicate = history.some((history) => history.identifier === item.identifier && history.ipType === item.ipType);

          if (!isDuplicate) {
            if (history.length >= 10) {
              history.pop();
            }

            history.unshift(item);

            const normalizedHistory: CommonIP[] = history.map((historyItem) => ({
              identifier: historyItem.identifier,
              ipType: historyItem.ipType,
              family: historyItem.family,
              title: historyItem.title ?? '',
              serial: historyItem.serial ?? '',
            }));

            localStorage.setItem(key, JSON.stringify(normalizedHistory));
            return of(HistoryActions.addHistoryItemSuccess({ items: normalizedHistory }));
          }

          return of(HistoryActions.addHistoryItemSuccess({ items: history }));
        } catch (error) {
          return of(HistoryActions.addHistoryItemFailure({ error }));
        }
      })
    )
  );
}
